import React, { useEffect, useMemo } from "react";

import { Allotment } from "allotment";
import "allotment/dist/style.css";

import { ContractorFormGroup } from "@app/components/contractors/contractorFormGroup";
import { InvoiceOverall } from "@app/components/invoiceGrid/maingrid";
import { InvoicePeek } from "@app/components/invoiceSPV/invoicePeek";
import "@app/components/invoiceSPV/invoices.scss";
import {
  SPVDetails,
  SPVExplanations,
} from "@app/components/invoiceSPV/spvDetails";
import { SPVFiles } from "@app/components/invoiceSPV/spvFiles";
import { SPVMessages } from "@app/components/invoiceSPV/spvList";
import { SpvPopup } from "@app/components/invoiceSPV/spvPopup";
import { SpvShortOps } from "@app/components/invoiceSPV/spvShortOps";
import {
  SPVFolderSelectToolbar,
  SPVToolbarTop,
} from "@app/components/invoiceSPV/spvToolbar";
import { CollectForm } from "@app/components/invoiceTrack/collectForm";
import { MonthExplorerV2 } from "@app/components/monthTree";
import { TreeSwitch } from "@app/components/monthTree/treeSwitch";
import {
  Case,
  CaseElse,
  Switch,
  useAllotmentRef,
  useComponentStack,
  useWindowDimensions,
} from "@app/contexts";
import { EComponentStack, SIZES, SPV_RIGHT_TABS } from "@app/state/constants";
import { useFxDispatch, useFxSelector } from "@app/statefx";
import { schema } from "@app/statefx/schema";
import { EUIIMainSOT } from "@app/statefx/schema/uiInvoiceMain.def";
import { setToolbarTopTab } from "@app/statefx/xThunks/uiInvoiceOverall";

type TSPVRT = typeof SPV_RIGHT_TABS;

export const InvoiceMain = () => {
  const dispatch = useFxDispatch();
  ///Setting this IN JGAP, pay attention on copy-paste
  const { components: stack } = useComponentStack(
    EComponentStack.InvoiceOverall
  );

  const { selectedRightTab } = useFxSelector(schema.uiSpvGrid.select);
  const uiInvoiceSettings = useFxSelector(schema.uiInvoiceOverall.select);

  const alloSizes = useFxSelector(schema.uiAllotment.select);
  const collapsedStates = useFxSelector(schema.uiAllotmentCollapsed.select);

  const whoisactive = useMemo(() => {
    // keep this to make the allotmet sizing work
    return EComponentStack.InvoiceOverall;
  }, [stack]);

  // const data = useFxSelector(selectCurentInvoiceForPayments);
  const {
    alltRef,
    rightRef,
    currentCollapsedState: detailsAreCollapsed,
    markPanePosition,
  } = useAllotmentRef();

  const {
    height: fullHeight,
    headersFooters,
    width,
    rightReserved,
    jgDrawerHeight,
    appIsPersonalKarma,
  } = useWindowDimensions();

  const isSpvMessage = useMemo(
    () => stack.includes(EComponentStack.SpvMessages),
    [stack]
  );
  useEffect(() => {
    // Dispatch action to set the correct tab only when the app is JGAP
    if (process.env.REACT_APP_APP === "JGAP") {
      const tab = isSpvMessage ? SPV_RIGHT_TABS.FILE : SPV_RIGHT_TABS.PAYMENTS;
      dispatch(setToolbarTopTab(tab));
    }
  }, [isSpvMessage, appIsPersonalKarma]);

  // const oneQuarter = (width - rightReserved) / 4;
  // const MemSpvFiles = React.memo(SPVFiles);
  // const SPVMessagesMemo = React.memo(SPVMessages);
  return (
    <div
      id={"contractors-page"}
      style={
        {
          height: `${jgDrawerHeight}px`,
          "--headerButtonWidth": `${SIZES.headerButtonSize}px`,
        } as React.CSSProperties
      }
    >
      <Allotment
        ref={alltRef}
        snap
        onDragStart={(e) => {
          markPanePosition("start", e);
        }}
        onDragEnd={(e) => {
          markPanePosition("end", e);
        }}
      >
        <Allotment.Pane key={"grid"} prefferedSize={alloSizes[whoisactive][0]}>
          <div id={"invoiceTrack"}>
            <TreeSwitch />
            <MonthExplorerV2 parent={"InvoiceMain"} />
            <div id={"invoiceTrackForm"}>
              <Switch value={uiInvoiceSettings.mainSOT}>
                <Case when={EUIIMainSOT.SPV_OVERALL}>
                  <SPVMessages />
                </Case>
                <Case when={EUIIMainSOT.FACTURI_FISCALE_EMISE}>
                  <InvoiceOverall />
                </Case>
                <CaseElse>
                  <div>not implemented</div>
                </CaseElse>
              </Switch>
            </div>
          </div>
        </Allotment.Pane>
        <Allotment.Pane
          key={"forms"}
          minSize={110}
          // {Math.max(500, oneQuarter)}
          snap={false}
          ref={rightRef}
        >
          <div
            style={{
              height: "100%",
              padding: "2px",
            }}
          >
            <div id="mspv-command">
              <SPVToolbarTop role={"Header"} />
            </div>
            <Switch value={detailsAreCollapsed}>
              <Case when={true}>
                <SPVToolbarTop role={"VerticalTabsCollapsed"} />
              </Case>
              <Case when={false}>
                <Switch value={selectedRightTab}>
                  <Case when={SPV_RIGHT_TABS.FILE}>
                    <div id="mspv-filesys">
                      <SpvShortOps />
                      <InvoicePeek />
                      <SPVDetails />
                      <SPVExplanations />
                      <div>files</div>
                    </div>
                    <div id="mspv-details"></div>
                  </Case>
                  <Case when={SPV_RIGHT_TABS.CONTRACTOR}>
                    <div id="mspv-filesys">
                      <ContractorFormGroup />
                    </div>
                    <div id="mspv-details"></div>
                  </Case>
                  <Case when={SPV_RIGHT_TABS.OPERATIONS}>
                    <div id="mspv-filesys">
                      <div>{"operations"}</div>
                      <SPVFolderSelectToolbar />
                    </div>
                    <div id="mspv-details"></div>
                  </Case>
                  <Case when={SPV_RIGHT_TABS.PAYMENTS}>
                    <div id="mspv-filesys">
                      <CollectForm />
                    </div>
                  </Case>
                </Switch>
              </Case>
            </Switch>
          </div>
        </Allotment.Pane>
      </Allotment>
      <SpvPopup />
    </div>
  );
};
